import React from 'react';
import { ArrowRightIcon, ArrowUpIcon, ClockIcon, FileTextIcon, AlertTriangleIcon, MapPinIcon, SparklesIcon } from 'lucide-react';
import { priorityCards, compliance, statusSplit, revenue, recommendation, type Bi, type Tone } from '../../../data/animalsIntelligence';
type P = {
  isFR: boolean;
};
const pk = (isFR: boolean) => (v: Bi) => isFR ? v.fr : v.en;
export const PANEL = 'rounded-[18px] border border-[#EEEEF4] bg-white shadow-[0_12px_30px_-26px_rgba(40,36,120,0.45)]';
const TONES: Record<Tone, {
  chip: string;
  icon: string;
  btn: string;
}> = {
  urgent: {
    chip: 'bg-[#FDECEC]',
    icon: 'text-[#E5484D]',
    btn: 'bg-[#FDECEC] text-[#D93A3F] hover:bg-[#FBDEDE]'
  },
  warning: {
    chip: 'bg-[#FEF4DF]',
    icon: 'text-[#E09A12]',
    btn: 'bg-[#FEF4DF] text-[#B87708] hover:bg-[#FDECC7]'
  },
  indigo: {
    chip: 'bg-[#EEEDFD]',
    icon: 'text-[#4F46E5]',
    btn: 'bg-[#EEEDFD] text-[#4F46E5] hover:bg-[#E3E1FC]'
  }
};
const PRIORITY_ICONS = {
  expiring: ClockIcon,
  incomplete: FileTextIcon,
  expired: AlertTriangleIcon,
  sector: MapPinIcon
} as const;
export function PriorityCards({
  isFR
}: P) {
  const pick = pk(isFR);
  return <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3" data-id="element-404" data-mp="386qevldxdahj">
      {priorityCards.map(c => {
      const Icon = PRIORITY_ICONS[c.key];
      const t = TONES[c.tone];
      const isText = c.key === 'sector';
      return <li key={c.key} className={`${PANEL} p-3.5 flex flex-col`} data-id="element-405" data-mp="3h1xw8bmgymcg">
            <div className="flex items-start gap-3" data-id="element-406" data-mp="2kavycdon9tg2">
              <span className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${t.chip}`} aria-hidden="true" data-id="element-407" data-mp="2p8dzhhwf74di">
                <Icon className={`w-5 h-5 ${t.icon}`} strokeWidth={1.8} data-id="element-408" data-mp="2t97nscdryl6x" />
              </span>
              <div className="min-w-0" data-id="element-409" data-mp="2yaex4fvkfsfp">
                <p className={`font-sans font-semibold text-[#121A3A] leading-tight ${isText ? 'text-[16px]' : 'text-[24px]'}`} data-id="element-410" data-mp="2y796gw7agihv">{pick(c.value)}</p>
                <p className="font-sans text-[11.5px] text-[#5B6478] leading-snug mt-0.5" data-id="element-411" data-mp="dibqc2vhbajo">{pick(c.label)}</p>
              </div>
            </div>
            <button type="button" className={`mt-auto pt-0 w-full inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 font-sans text-[11.5px] font-semibold transition-colors duration-150 ${t.btn}`} style={{
          marginTop: 12
        }} data-id="element-412" data-mp="29kg8idl1ojr6">
              {pick(c.cta)}
              <ArrowRightIcon className="w-3 h-3" strokeWidth={2.4} aria-hidden="true" data-id="element-413" data-mp="2emdu3egx3qna" />
            </button>
          </li>;
    })}
    </ul>;
}
function ComplianceChart() {
  const w = 200;
  const h = 64;
  const pts = compliance.points.map((v, i) => [8 + i * (w - 16) / (compliance.points.length - 1), h - v / 80 * h] as const);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join(' ');
  const area = `${line} L${pts[pts.length - 1][0]},${h} L${pts[0][0]},${h} Z`;
  return <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[64px]" aria-hidden="true" data-id="element-414" data-mp="183z5wtcy0fmz">
      <path d={area} fill="#EEEDFD" data-id="element-415" data-mp="1355tl49e6uoe" />
      <path d={line} fill="none" stroke="#4F46E5" strokeWidth="1.8" data-id="element-416" data-mp="13433gyjw8w9j" />
      {pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.8" fill="#fff" stroke="#4F46E5" strokeWidth="1.6" data-id="element-417" data-mp="3erg1l3zryfss" data-mpi={i} />)}
    </svg>;
}
export function CompliancePanel({
  isFR
}: P) {
  const pick = pk(isFR);
  const months = isFR ? compliance.months.fr : compliance.months.en;
  return <div className={`${PANEL} p-4`} data-id="element-418" data-mp="39v95s413ekok">
      <p className="font-sans text-[12.5px] font-semibold text-[#121A3A]" data-id="element-419" data-mp="39s3dyh12x4p5">{pick(compliance.title)}</p>
      <div className="mt-2 flex items-end gap-2.5" data-id="element-420" data-mp="1cte7fknwbkj">
        <span className="font-sans text-[28px] font-semibold text-[#121A3A] leading-none" data-id="element-421" data-mp="6cfvc03m2bym">{pick(compliance.value)}</span>
        <span className="font-sans text-[10.5px] leading-tight" data-id="element-422" data-mp="3oj2m5qy05r87">
          <span className="flex items-center gap-0.5 font-semibold text-[#1E8A45]" data-id="element-423" data-mp="3thdu1w4qlrns">
            <ArrowUpIcon className="w-3 h-3" strokeWidth={2.4} aria-hidden="true" data-id="element-424" data-mp="2d3noekxyyvc1" />
            {pick(compliance.trend)}
          </span>
          <span className="text-[#8A90A2]" data-id="element-425" data-mp="2i4uv0godloh4">{pick(compliance.vs)}</span>
        </span>
      </div>
      <div className="mt-3" data-id="element-426" data-mp="2xehu0b5sgl6t">
        <ComplianceChart data-id="element-427" data-mp="2xerly4ae92f5" />
        <div className="flex justify-between px-1 mt-1 font-sans text-[10px] text-[#8A90A2]" data-id="element-428" data-mp="2sloovm6erau7">
          {months.map(m => <span key={m} data-id="element-429" data-mp="2nnwxqudl7sr2">{m}</span>)}
        </div>
      </div>
    </div>;
}
export function StatusPanel({
  isFR
}: P) {
  const pick = pk(isFR);
  const r = 36;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return <div className={`${PANEL} p-4`} data-id="element-430" data-mp="3g9oq5g1cijn1">
      <p className="font-sans text-[12.5px] font-semibold text-[#121A3A]" data-id="element-431" data-mp="3bcy1i4bh6fq0">{pick(statusSplit.title)}</p>
      <div className="mt-3 flex items-center gap-4" data-id="element-432" data-mp="37al546imbu5w">
        <div className="relative w-[104px] h-[104px] shrink-0" data-id="element-433" data-mp="3799tjkixli48">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90" aria-hidden="true" data-id="element-434" data-mp="32ayi55czmzjw">
            {statusSplit.items.map(s => {
            const len = s.pct / 100 * circ;
            const el = <circle key={s.key} cx="50" cy="50" r={r} fill="none" stroke={s.color} strokeWidth="11" strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-offset} data-id="element-435" data-mp="1qvtaro1wwcty" />;
            offset += len;
            return el;
          })}
          </svg>
          <span className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-436" data-mp="bbj1gvs1k4ei">
            <span className="font-sans text-[15px] font-semibold text-[#121A3A] leading-none" data-id="element-437" data-mp="fde17vubq9q6">{pick(statusSplit.total)}</span>
            <span className="font-sans text-[10px] text-[#8A90A2] mt-0.5" data-id="element-438" data-mp="p5jz1m79blda">{pick(statusSplit.unit)}</span>
          </span>
        </div>
        <ul className="space-y-2" data-id="element-439" data-mp="u33e2obs426a">
          {statusSplit.items.map(s => <li key={s.key} className="flex items-center gap-2 font-sans text-[11.5px] text-[#3A4258]" data-id="element-440" data-mp="z37w4isc54ym">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{
            background: s.color
          }} aria-hidden="true" data-id="element-441" data-mp="135cozfaylxk7" />
              <span className="font-semibold" data-id="element-442" data-mp="30w5s2nhesdns">{s.pct}{isFR ? ' %' : '%'}</span>
              <span data-id="element-443" data-mp="34vo5ky6whmgl">{pick(s.label)}</span>
            </li>)}
        </ul>
      </div>
    </div>;
}
export function RevenuePanel({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${PANEL} relative p-4 overflow-hidden flex flex-col`} data-id="element-444" data-mp="73e7avht21gg">
      <div className="absolute right-4 top-[46px] flex items-end gap-1.5 h-[54px]" aria-hidden="true" data-id="element-445" data-mp="l2nre694tfpp">
        {[22, 34, 28, 44, 54].map((h, i) => <span key={i} className="w-3.5 rounded-t-sm bg-[#E4E2FB]" style={{
        height: h
      }} data-id="element-446" data-mp="h098xn9dkhyy" data-mpi={i} />)}
      </div>
      <p className="relative font-sans text-[12.5px] font-semibold text-[#121A3A]" data-id="element-447" data-mp="uyz4gygjq9l7">{pick(revenue.title)}</p>
      <p className="relative mt-2 font-sans text-[28px] font-semibold text-[#121A3A] leading-none" data-id="element-448" data-mp="pzmiefxi16jo">{pick(revenue.value)}</p>
      <p className="relative mt-2 font-sans text-[11px] text-[#5B6478] leading-snug max-w-[62%]" data-id="element-449" data-mp="zrjtznsf60ix">{pick(revenue.body)}</p>
      <button type="button" className="relative mt-auto self-start inline-flex items-center gap-1.5 rounded-lg bg-[#EEEDFD] px-3 py-2 font-sans text-[11.5px] font-semibold text-[#4F46E5] hover:bg-[#E3E1FC] transition-colors duration-150" style={{
      marginTop: 14
    }} data-id="element-450" data-mp="1dqrxutzj1wtg">
        {pick(revenue.cta)}
        <ArrowRightIcon className="w-3 h-3" strokeWidth={2.4} aria-hidden="true" data-id="element-451" data-mp="1xdms4kvcicm5" />
      </button>
    </div>;
}
export function RecommendationPanel({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className="rounded-[18px] border border-[#E2E0FA] bg-[#F4F3FE] p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-id="element-452" data-mp="1nee03xk1j82f">
      <div className="min-w-0 flex-1" data-id="element-453" data-mp="19mqtb3sh2h5e">
        <p className="flex items-center gap-2 font-sans text-[17px] font-semibold" data-id="element-454" data-mp="19lpin0fdbwo7">
          <SparklesIcon className="w-5 h-5 text-[#4F46E5] fill-[#4F46E5]" strokeWidth={1.5} aria-hidden="true" data-id="element-455" data-mp="1egbuf4pd3r8t" />
          <span className="text-[#4F46E5]" data-id="element-456" data-mp="zni40o8jxqpd">{pick(recommendation.header1)}</span>
          <span className="text-[#121A3A] -ml-2" data-id="element-457" data-mp="14opckpiiqtx3">{pick(recommendation.header2)}</span>
        </p>
        <p className="mt-2 font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-458" data-mp="qpg2m7kwow1k">{pick(recommendation.priority)}</p>
        <p className="mt-1 font-sans text-[12.5px] text-[#3A4258] leading-snug" data-id="element-459" data-mp="vpkc3dnecqia">{pick(recommendation.body)}</p>
      </div>
      <button type="button" className="shrink-0 self-start lg:self-center inline-flex items-center gap-2 rounded-full bg-[#4F46E5] px-5 py-2.5 font-sans text-[13px] font-semibold text-white whitespace-nowrap shadow-[0_12px_24px_-12px_rgba(79,70,229,0.7)] hover:bg-[#4338CA] transition-colors duration-150" data-id="element-460" data-mp="166xn3xn98x8c">
        {pick(recommendation.cta)}
        <ArrowRightIcon className="w-3.5 h-3.5" strokeWidth={2.4} aria-hidden="true" data-id="element-461" data-mp="s8zktqm7kr97" />
      </button>
    </div>;
}